/**
 * Application Deadline Section Component
 * Displays application deadline or estimated expiry date
 */

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CalendarBlank } from '@phosphor-icons/react';

export interface ApplicationDeadlineSectionProps {
  formattedApplicationDate: string | null;
  formattedEstimatedExpiryDate: string | null;
  formattedPostedDate: string | null;
  postedDaysAgo: number | null;
  formattedLastSeenDate: string | null;
  lastSeenDaysAgo: number | null;
}

export const ApplicationDeadlineSection: React.FC<ApplicationDeadlineSectionProps> = ({
  formattedApplicationDate,
  formattedEstimatedExpiryDate,
  formattedPostedDate,
  postedDaysAgo,
  formattedLastSeenDate,
  lastSeenDaysAgo,
}) => {
  const intl = useIntl();
  const lastSeenAgoText = lastSeenDaysAgo === null
    ? ''
    : ` (${intl.formatMessage(
      {
        id: 'jobDetailPage.lastSeenAgoCompact',
        defaultMessage: '{days, plural, =0 {today} one {#d ago} other {#d ago}}',
        description: 'Compact relative time suffix for last seen date in days',
      },
      { days: lastSeenDaysAgo },
    )})`;

  if (!formattedPostedDate && !formattedLastSeenDate && !formattedApplicationDate && !formattedEstimatedExpiryDate) {
    return null;
  }

  const hasExplicitDeadline = !!formattedApplicationDate;
  const deadlineLabel = hasExplicitDeadline
    ? <FormattedMessage id="jobDetailPage.applicationDeadline" defaultMessage="Application deadline" description="Section label for job application deadline date" />
    : <FormattedMessage id="jobDetailPage.estimatedExpiry" defaultMessage="Estimated expiry" description="Section label for estimated job posting expiry date" />;
  const deadlineValue = formattedApplicationDate || formattedEstimatedExpiryDate;

  return (
    <div className="p-2.5 rounded-xl bg-[#F5EDD0]/50 dark:bg-[#2A1E08]/50 border border-[#B8860B]/25 dark:border-[#4A3520] shadow-sm dark:shadow-none">
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 shrink-0 rounded-lg bg-[#F5EDD0] dark:bg-[#342415] flex items-center justify-center border border-[#B8860B]/20 dark:border-[#4A3520]">
          <CalendarBlank className="h-4 w-4 text-[#6E4F00] dark:text-[#D4A820]" weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold text-[#6E4F00] dark:text-[#D4A820] uppercase tracking-wide">
            <FormattedMessage id="hit.postedDate.detail" defaultMessage="Posted: {date}" values={{ date: formattedPostedDate ?? '—' }} description="Job posting date shown on job detail page" />
          </div>
          {postedDaysAgo !== null ? (
            <div className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] leading-snug mt-0.5">
              <FormattedMessage
                id="jobDetailPage.postedAgo"
                defaultMessage="{days, plural, =0 {Posted today} one {Posted 1 day ago} other {Posted # days ago}}"
                description="Relative posted time in days"
                values={{ days: postedDaysAgo }}
              />
            </div>
          ) : null}
          {formattedLastSeenDate ? (
            <div className="text-[11px] text-[#5A4A3A] dark:text-[#C4A882] mt-1">
              <FormattedMessage
                id="jobDetailPage.lastSeenDetail"
                defaultMessage="Last seen in crawl: {date}{ago}"
                description="Last seen crawl timestamp for this job"
                values={{
                  date: formattedLastSeenDate,
                  ago: lastSeenAgoText,
                }}
              />
            </div>
          ) : null}
          {deadlineValue ? (
            <div className="text-[11px] text-[#7A6E5F] dark:text-[#B8A48C] mt-1">
              {deadlineLabel}: {deadlineValue}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
